import {
  ButtonProps,
  Loading,
  PrimaryButton,
  SecondaryButton,
  Tooltip,
} from '@shape-ds/components';
import { usePermissionsQuery } from 'queries/userAndPermissions';
import React from 'react';
import { useTranslation } from 'react-i18next';

type PermissionedButtonProps = ButtonProps & {
  buttonVariant?: 'primary' | 'secondary';
  adminOnly?: boolean;
  isLoading?: boolean;
  overrideDisableTooltip?: string;
};

const PermissionedButton: React.FC<PermissionedButtonProps> = ({
  children,
  buttonVariant = 'primary',
  adminOnly = false,
  isLoading = false,
  overrideDisableTooltip,
  ...props
}) => {
  const { t } = useTranslation();
  const ButtonElement = buttonVariant === 'primary' ? PrimaryButton : SecondaryButton;
  const { data, isLoading: isLoadingPermissions } = usePermissionsQuery();

  const isAdmin = data?.permissions.includes('ADMIN');
  const hasWrite = data?.permissions.includes('READ_WRITE');

  if (adminOnly && (!isAdmin || !hasWrite)) {
    return null;
  }

  if (!hasWrite || (overrideDisableTooltip && props.disabled)) {
    return (
      <Tooltip
        followCursor={false}
        placement="top"
        dataTestId="permissioned-button-tooltip"
        title={
          <span>
            {!hasWrite ? t('components:permissioned_button.unauthorized') : overrideDisableTooltip}
          </span>
        }
      >
        <ButtonElement {...props} disabled>
          {children}
        </ButtonElement>
      </Tooltip>
    );
  }
  return (
    <ButtonElement {...props}>
      {isLoading || isLoadingPermissions ? <Loading size="16px" /> : children}
    </ButtonElement>
  );
};

export default PermissionedButton;
